import { useCallback, useMemo, useState } from 'react';

import { useAppService } from '../../providers/app/app.context';
import { useCurrentBusinessPartnerDivisions } from '../../pages/Divisions/divisions.queries';
import type { ListQueryParams } from '../useListState';

export type DivisionScope = 'currentAndChildren' | 'current' | 'children';

/** `ListQueryParams` plus the Division rollup's scope and selection, for the
 * list queries that are rolled up across Divisions (Customers, Invites). */
export interface DivisionScopedListQueryParams extends ListQueryParams {
  scope: DivisionScope;
  selectedDivisionIds: string[];
}

export interface DivisionSummary {
  id: string;
  name: string;
}

export interface UseDivisionRollupReturn {
  /** The Divisions query has settled (succeeded or failed), so `scope` holds
   * its real default and a page's own query can start. */
  isReady: boolean;
  /** Non-empty Divisions list. A failed Divisions query counts as `false`
   * (PRD D5). */
  hasDivisions: boolean;
  divisions: DivisionSummary[];
  scope: DivisionScope;
  /** Also clears `selectedDivisionIds`, so switching the quick filter away
   * from a Division selection (e.g. back to Organization) doesn't keep
   * sending a stale Division filter alongside the new scope. */
  setScope: (scope: DivisionScope) => void;
  selectedDivisionIds: string[];
  /** Applies the Division filter drawer's selection. Switches `scope` to
   * `'children'` when the selection is non-empty (PRD 02 "Division rollup"). */
  applyDivisions: (divisionIds: string[]) => void;
  /** Clears the selection only; `scope` is left alone so Clear filters
   * doesn't move the quick filter (`'children'` with no ids is the
   * "Division" quick filter: all Divisions, own rows excluded). */
  clearDivisions: () => void;
  /** Composite of `scope` and `selectedDivisionIds` (`'children:a,b'`), for
   * `useListState({ resetKey })` so pages don't re-derive it. */
  filterKey: string;
  /** Resolves the Division column for a row: the active Organization's own
   * name, a listed Division's name, or `undefined` for a missing or
   * unrecognised provider (PRD D5), which the cell renders as its unknown
   * placeholder. */
  resolveDivisionName: (providerBusinessPartnerId: string | undefined) => string | undefined;
  /** Whether a row belongs to the active Business Partner itself, as opposed
   * to one of its Divisions (PRD D13 / D4: own rows keep their actions,
   * Division rows have none). */
  isOwnRow: (providerBusinessPartnerId: string | undefined) => boolean;
}

/**
 * Division rollup shared by Customers (02) and Invites (03): wraps
 * `useCurrentBusinessPartnerDivisions()` and the active Business Partner into
 * the quick filter's scope, the Division multiselect filter and per-row
 * Division-name resolution (PRD D4–D6).
 */
export function useDivisionRollup(): UseDivisionRollupReturn {
  const { currentBusinessPartner } = useAppService();
  const divisionsQuery = useCurrentBusinessPartnerDivisions();

  const isReady = !divisionsQuery.isLoading;

  const divisions = useMemo<DivisionSummary[]>(
    () => (divisionsQuery.data?.items ?? []).map(({ id, name }) => ({ id, name })),
    [divisionsQuery.data]
  );

  const hasDivisions = divisionsQuery.isError ? false : divisions.length > 0;

  // The default scope depends on whether the Organization has Divisions, so
  // it is derived from the Divisions query rather than stored: the render in
  // which `isReady` flips already carries the right `scope`. A user's own
  // choice is kept as an override and, once set, wins over later refetches.
  const [scopeOverride, setScopeOverride] = useState<DivisionScope | null>(null);
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);

  const scope = scopeOverride ?? (hasDivisions ? 'currentAndChildren' : 'current');

  // Also clears `selectedDivisionIds`: switching the quick filter away from
  // a Division selection must not leave a stale Division filter applied
  // alongside the new scope (e.g. back to Organization after Apply).
  const setScope = useCallback((nextScope: DivisionScope) => {
    setSelectedDivisionIds([]);
    setScopeOverride(nextScope);
  }, []);

  const applyDivisions = useCallback((divisionIds: string[]) => {
    setSelectedDivisionIds(divisionIds);
    if (divisionIds.length > 0) setScopeOverride('children');
  }, []);

  const clearDivisions = useCallback(() => {
    setSelectedDivisionIds([]);
  }, []);

  const filterKey = `${scope}:${selectedDivisionIds.join(',')}`;

  const isOwnRow = useCallback(
    (providerBusinessPartnerId: string | undefined) =>
      !!providerBusinessPartnerId && providerBusinessPartnerId === currentBusinessPartner?.id,
    [currentBusinessPartner]
  );

  const resolveDivisionName = useCallback(
    (providerBusinessPartnerId: string | undefined) => {
      if (!providerBusinessPartnerId) return undefined;
      if (providerBusinessPartnerId === currentBusinessPartner?.id) {
        return currentBusinessPartner?.name;
      }
      return divisions.find(({ id }) => id === providerBusinessPartnerId)?.name;
    },
    [currentBusinessPartner, divisions]
  );

  return {
    isReady,
    hasDivisions,
    divisions,
    scope,
    setScope,
    selectedDivisionIds,
    applyDivisions,
    clearDivisions,
    filterKey,
    resolveDivisionName,
    isOwnRow,
  };
}
